import React, { useEffect, useMemo, useRef, useState } from 'react'
import { StyledForm, StyledFormUpload } from './style'
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants'
import { Button, Input, message, Select, Upload, UploadProps } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import useTags from '../../../hooks/common/useTags'
import CustomLoading from '../../Common/Loading'
import { ForumTopicAttributesType, TagsType } from '../../../types'
import Tiptap from '../../Common/Form/Tiptap'
import useListAllForumTopics, { useCreateForumTopic } from '../../../hooks/common/useForums'
import useListCommunitySubgroups from '../../../hooks/admin/useCommunitySubgroups'
import { useParams } from 'react-router-dom'

type CommunityFormProps = {
  type?: 'new' | 'edit'
  defaultValues?: Partial<ForumTopicAttributesType>
  onReset?: boolean
  onAfterSubmit: () => void
}

const { Option } = Select;

const CommunityForm: React.FC<CommunityFormProps> = ({
  type,
  onReset,
  defaultValues,
  onAfterSubmit
}) => {
  const { code } = useParams();
  const [form] = StyledForm.useForm();
  const { data: tags, loading: loadingTags } = useTags();
  const { data: communitySubgroups, loading: loadingCommunitySubgroups } = useListCommunitySubgroups();
  const { create: createForumTopic, loading: loadingCreateForumTopic } = useCreateForumTopic();
  const { refetch: refetchListAllForumTopics } = useListAllForumTopics();
  const tiptapRef = useRef<{ clearContent: () => void }>(null);
  const [description, setDescription] = useState<string>(type === 'edit' ? defaultValues?.body! : '');
  const [previewCoverImage, setPreviewCoverImage] = useState<string | null>(null);

  const coverImageDropZoneProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: 'image/*',
    showUploadList: false,
    onChange(info) {
      const { status } = info.file;
      if (status !== 'uploading') {
      }
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return Upload.LIST_IGNORE;
      }

      const previewUrl = URL.createObjectURL(file);
      setPreviewCoverImage(previewUrl);
      return false;
    },
    onRemove() {
      setPreviewCoverImage(null);
    },
    onDrop(e) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      console.log('Dropped files', droppedFiles);
    }
  };

  const attachmentsDropZoneProps: UploadProps = {
    name: 'file',
    multiple: true,
    onChange(info) {
      const { status } = info.file;
      if (status !== 'uploading') {
      }
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    beforeUpload: (file, fileList) => {
      return false;
    },
    onRemove(file) {
    },
    onDrop(e) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      console.log('Dropped files', droppedFiles);
    },
    defaultFileList: []
  };

  const options = tags?.map((tag: TagsType) => ({
    label: tag.name,
    value: tag.id
  }));

  const subgroupsByCodeOptions = useMemo(() => {
    const subgroups = communitySubgroups?.filter((subgroup: any) =>
      subgroup.attributes.members.some((member: any) => member.code === code)
    );

    return subgroups?.map((subgroup: any) => ({
      label: subgroup.attributes.name,
      value: subgroup.id
    }));
  }, [communitySubgroups, code]);

  const onChangeDescription = (value: string) => {
    setDescription(value);
  };

  const onFinish = async (values: any) => {
    try {
      const payload = {
        ...values,
        coverImage: values.coverImage.file,
        attachments: values.attachments.fileList.map((file: any) => file.originFileObj)
      }
      const res = await createForumTopic(payload);
      if (res.success) {
        form.resetFields();
        await refetchListAllForumTopics();
        onAfterSubmit();
      }
    } catch (error: any) {
      console.error(error)
    }
  };

  useEffect(() => {
    if (!onReset) {
      form.resetFields();
    }
  }, [onReset, form]);

  if (loadingTags || loadingCommunitySubgroups) {
    return <CustomLoading />
  }

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="community_form"
      onFinish={onFinish}
      initialValues={{}}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      <StyledForm.Item
        labelAlign="left"
        label=""
        name="coverImage"
      >
        <StyledFormUpload {...coverImageDropZoneProps}>
          {previewCoverImage ? (
            <img
              src={previewCoverImage}
              alt="biocoherence-community-coverimage"
              style={{
                width: '100%',
                maxHeight: 300,
                objectFit: 'contain'
              }}
            />
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Drop your image here or browse</p>
              <p className="ant-upload-hint">
                Horizontal images will give a better result. Best ratio: Height 1, width 3.
              </p>
              <p className="ant-upload-hint">
                Supports: JPG & PNG
              </p>
            </>
          )}
        </StyledFormUpload>
      </StyledForm.Item >
      <StyledForm.Item
        labelAlign="left"
        label="Category"
        name="category"
        hasFeedback
        rules={[{ required: true, message: 'Please select a category' }]}
      >
        <Select placeholder="Please select">
          <Option value="question">Question</Option>
          <Option value="idea">Idea</Option>
          <Option value="success_story">Success Story</Option>
        </Select>
      </StyledForm.Item>
      <StyledForm.Item
        labelAlign="left"
        label="Subgroup"
        name="communitySubgroupId"
        hasFeedback
        rules={[{ required: true, message: 'Please select a Subgroup' }]}
      >
        <Select
          placeholder="Please select sub-group"
          allowClear
          options={subgroupsByCodeOptions}
        />
      </StyledForm.Item>
      <StyledForm.Item
        labelAlign="left"
        label="Tags"
        name="tags"
        hasFeedback
        rules={[{ required: true, message: 'Please select a Subgroup' }]}
      >
        <Select
          mode="multiple"
          allowClear
          placeholder="Please select tags"
          options={options}
        />
      </StyledForm.Item>
      <StyledForm.Item
        labelAlign="left"
        label="Title"
        name="title"
        hasFeedback
        rules={[{ required: true, message: 'Please enter a Title' }]}
      >
        <Input />
      </StyledForm.Item>
      <StyledForm.Item
        labelAlign="left"
        name="body"
        label=""
        rules={[
          {
            required: true,
            message: 'Body can\'t be blank',
          },
        ]}
      >
        <Tiptap
          ref={tiptapRef}
          content={description}
          onChange={onChangeDescription}
        />
      </StyledForm.Item>
      <label className="ant-form-item">Attachments: </label>
      <StyledForm.Item
        labelAlign="left"
        label=""
        name="attachments"
      >
        <StyledFormUpload {...attachmentsDropZoneProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to upload</p>
          <p className="ant-upload-hint">
            Support for a single or bulk upload. Strictly prohibited from uploading company data or other
            banned files.
          </p>
        </StyledFormUpload>
      </StyledForm.Item>
      <StyledForm.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button loading={loadingCreateForumTopic} type="primary" htmlType="submit">
          Save
        </Button>
      </StyledForm.Item>
    </StyledForm >
  )
}

export default CommunityForm